import React, { useMemo } from 'react';
import { Moon } from 'lucide-react';
import { useDarkMode } from '../../../contexts/DarkModeContext';
import { useFieldLevelSetting } from './hooks/useFieldLevelSetting';
import { TriggerPicker } from './components/TriggerPicker';
import { sectionStyles } from './styles';
import {
  SCREEN_MODES,
  describeTrigger,
  parseScreenSchedule,
  serializeScreenSchedule,
} from '../../../utils/screenSchedule';
import { screenScheduleStrings } from '../../../utils/screenScheduleStrings';

/** "At night" card: writes only `simpleDashboard.screenSchedule` (all four fields). */
export const NightScreenSettings = ({ onSuccess, onError }) => {
  const { isDarkMode } = useDarkMode();
  const styles = sectionStyles(isDarkMode);
  const strings = useMemo(() => screenScheduleStrings(navigator.language || 'en'), []);
  const isRtl = (navigator.language || '').toLowerCase().startsWith('ar');

  const [schedule, change] = useFieldLevelSetting('screenSchedule', parseScreenSchedule, serializeScreenSchedule, {
    onSuccess, onError, savedMessage: strings.saved,
  });

  const prayerName = (p) => strings.prayers[p] || p;
  const subtitle = schedule.enabled
    ? `${strings.modes[schedule.mode].name}, ${describeTrigger(schedule.offTrigger, prayerName, strings.minutes)} – ${describeTrigger(schedule.onTrigger, prayerName, strings.minutes)}`
    : strings.off;

  const disabled = !schedule.enabled;
  const divider = isDarkMode ? 'border-white/10' : 'border-gray-200';
  const muted = isDarkMode ? 'text-gray-400' : 'text-gray-600';

  return (
    <section className={`${styles.container} relative z-0`} dir={isRtl ? 'rtl' : undefined}>
      <div className={styles.header}>
        <Moon className={styles.headerIcon} />
        <h2 className={styles.headerText}>{strings.title}</h2>
      </div>
      <div className={styles.card}>
        <label className="flex items-center justify-between gap-4 cursor-pointer">
          <div>
            <div className="text-base font-medium">{strings.toggle}</div>
            <div className={`text-sm ${muted}`}>{subtitle}</div>
          </div>
          <input
            type="checkbox"
            role="switch"
            aria-checked={schedule.enabled}
            className="h-5 w-5 accent-emerald-600"
            checked={schedule.enabled}
            onChange={(e) => change({ enabled: e.target.checked }, true)}
          />
        </label>

        <div className={`mt-4 pt-4 border-t ${divider} ${disabled ? 'opacity-50' : ''}`}>
          <div className="text-base font-medium mb-3">{strings.modeLabel}</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" role="radiogroup" aria-label={strings.modeLabel}>
            {SCREEN_MODES.map((mode) => {
              const active = schedule.mode === mode;
              return (
                <button
                  key={mode}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  disabled={disabled}
                  onClick={() => change({ mode }, true)}
                  className={`text-start p-4 rounded-xl transition-all duration-200 disabled:cursor-not-allowed ${
                    isDarkMode ? 'bg-[#1A1A1A]' : 'bg-gray-100'
                  } ${active ? (isDarkMode ? 'ring-2 ring-emerald-500' : 'ring-2 ring-emerald-600') : ''}`}
                >
                  <div className="text-base font-medium">{strings.modes[mode].name}</div>
                  <div className={`text-sm ${muted}`}>{strings.modes[mode].description}</div>
                </button>
              );
            })}
          </div>
        </div>

        <div className={`mt-4 border-t ${divider}`}>
          <TriggerPicker
            idPrefix="night-from"
            label={strings.from}
            trigger={schedule.offTrigger}
            disabled={disabled}
            strings={strings}
            onChange={(offTrigger) => change({ offTrigger })}
          />
          <TriggerPicker
            idPrefix="night-until"
            label={strings.until}
            trigger={schedule.onTrigger}
            disabled={disabled}
            strings={strings}
            onChange={(onTrigger) => change({ onTrigger })}
          />
        </div>

        <p className={`mt-4 pt-4 border-t ${divider} text-sm ${muted}`}>{strings.footer}</p>
      </div>
    </section>
  );
};
